import { UserOutlined, MenuOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { useEthers } from "@usedapp/core";
import { Col, Dropdown, Menu, Row, Drawer } from "antd";
import { IProfile, getUserBasicInfoAPI, logoutAPI } from "api/users/auth";
import { putProfileWalletAPI } from "api/users/profile";
import { ArrowDown } from "assets/common/svg/ArrowDown.icon";
import { NewLogo } from "assets/common/svg/admin/LogoAdminFull.icon";
import { ConnectWallet } from "assets/common/svg/users/profile-preview/ConnectWallet.icon";
import ConnectWalletModal from "components/module/connectWalletModal";
import { ethers } from "ethers";
import { useWallet } from "hooks/useWallet";
import Cookies from "js-cookie";
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { authActions } from "redux/slices/authSlice";
import { profileActions } from "redux/slices/profileSlice";
import { RootState } from "redux/store";
import { IMenuItems } from "types";
import { KEY_COOKIE, ROUTER_CONSTANTS } from "utils/constant";
import { clearStorageAndGlobalState, saveLocalStorage } from "utils/storage";
import { ActiveNetwork, Base, BaseSepolia } from "utils/connectors";
import { IHeaderProps } from "./header.props";
import {
  AvatarLogged,
  BtnConnectWallet,
  ButtonLogin,
  ButtonSignup,
  ConnectWalletContainer,
  Container,
  ItemMenuProfile,
  ItemMenuText,
  ItemMenuTextLogout,
  LoggedContainer,
  LoginContainer,
  LogoContainer,
  MenuItem,
  ProfileDropdown,
  ProfileText,
  ProfileTextContainer,
  MobileMenuButton,
  DrawerMenuLink,
  DesktopAuthWrapper,
  MobileAuthWrapper,
  DrawerMenuButton,
  MobileDrawerButton,
  DrawerExploreLink,
} from "./header.style";

const HeaderUser: FC<IHeaderProps> = ({
  onClickSignUp,
  onClickLogIn,
  ...props
}: IHeaderProps) => {
  let navigate = useNavigate();
  const dispatch = useDispatch();
  const [visibleWallet, setVisibleWallet] = useState<boolean>(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { balance, account, isConnected, connectToWallet, chainId } =
    useWallet();

  const currentNetworkName = useMemo(() => {
    if (!account) return null;
    if (chainId === Base.chainId) return Base.chainName;
    if (chainId === BaseSepolia.chainId) return BaseSepolia.chainName;
    return `Unknown (${chainId})`;
  }, [account, chainId]);
  const { deactivate } = useEthers();
  const { isLoggedIn } = useSelector((state: RootState) => state.authUser);
  const { basicInfo } = useSelector((state: RootState) => state.profileUser);
  const location = useLocation();
  const isActive = useCallback(
    (expectedPathName: string) => {
      if (location) {
        const pathName = location.pathname;

        if (expectedPathName === ROUTER_CONSTANTS.HOME)
          return pathName === ROUTER_CONSTANTS.HOME;

        return pathName.includes(expectedPathName);
      } else return false;
    },
    [location]
  );

  const { mutateAsync: updateWalletMutate } = useMutation(putProfileWalletAPI);

  useEffect(() => {
    const urlSearchParam = window.location.search
      ? new URLSearchParams(window.location.search.slice(1))
      : null;

    let hasAccessTokenParam = false;
    if (urlSearchParam) {
      const access_token = urlSearchParam.get(KEY_COOKIE.ACCESS_TOKEN);
      if (access_token) {
        clearStorageAndGlobalState(true);
        Cookies.set(KEY_COOKIE.ACCESS_TOKEN, access_token, {
          domain: process.env.REACT_APP_COOKIE_DOMAIN,
        });
        urlSearchParam.delete(KEY_COOKIE.ACCESS_TOKEN);
        hasAccessTokenParam = true;
      }
    }

    if (Cookies.get(KEY_COOKIE.ACCESS_TOKEN)) {
      getUserBasicInfoAPI()
        .then((user: IProfile) => {
          if (!isLoggedIn) {
            saveLocalStorage(
              KEY_COOKIE.LOGIN_INFO,
              JSON.stringify({
                isLoggedIn: true,
              })
            );
            dispatch(
              authActions.logIn({
                isLoggedIn: true,
              })
            );
          }
          saveLocalStorage(
            KEY_COOKIE.PROFILE,
            JSON.stringify({
              basic_info: user.basic_info,
              github_login: user.github_login,
            })
          );
          dispatch(
            profileActions.saveProfile({
              basicInfo: user.basic_info,
              githubLogin: user.github_login,
            })
          );
          if (urlSearchParam && hasAccessTokenParam) {
            window.location.href = `${window.location.origin}${
              window.location.pathname
            }${
              urlSearchParam.toString() ? `?${urlSearchParam.toString()}` : ""
            }`;
          }
        })
        .catch((error: any) => {
          if (error.response?.data?.message === "Unauthorized") {
            clearStorageAndGlobalState(true);
            dispatch(authActions.logOut());
            dispatch(profileActions.removeProfile());
          }
        });
    } else if (isLoggedIn) {
      dispatch(authActions.logOut());
      dispatch(profileActions.removeProfile());
      return;
    }
  }, [dispatch, isLoggedIn, navigate]);

  const menus: IMenuItems[] = [
    {
      name: "Blog",
      path: ROUTER_CONSTANTS.USER.BLOGS,
    },
    {
      name: "About",
      path: ROUTER_CONSTANTS.USER.ABOUT_US,
    },
  ];
  const menuRef = useRef<HTMLDivElement>(null);

  const switchWallet = async () => {
    if (account) {
      if (!isConnected) {
        await connectToWallet();
      } else {
        deactivate();
        window.location.reload();
      }
    } else {
      setVisibleWallet(true);
    }
  };

  useEffect(() => {
    if (isLoggedIn) updateWalletMutate(account);
  }, [account, updateWalletMutate, isLoggedIn]);

  const menuLogged = (
    <Menu
      items={[
        {
          key: "1",
          label: (
            <ConnectWalletContainer>
              <p>
                {account
                  ? isConnected
                    ? `${ethers.utils.formatEther(balance).slice(0, 7)} ETH`
                    : "Wrong Network"
                  : "Connect your wallet"}
              </p>
              {account && (
                <p
                  style={{
                    margin: "4px 0 8px",
                    fontSize: "12px",
                    color: isConnected ? "#52c41a" : "#ff4d4f",
                    fontWeight: 500,
                  }}
                >
                  🌐 {currentNetworkName}
                  {!isConnected && (
                    <span
                      style={{
                        color: "#ff4d4f",
                        display: "block",
                        marginTop: "2px",
                      }}
                    >
                      Expected: {ActiveNetwork.chainName}
                    </span>
                  )}
                </p>
              )}
              <BtnConnectWallet onClick={switchWallet}>
                <ConnectWallet />
                {account ? (
                  !isConnected ? (
                    <span>Switch Network</span>
                  ) : (
                    <span>Disconnect wallet</span>
                  )
                ) : (
                  <span>Connect wallet</span>
                )}
              </BtnConnectWallet>
            </ConnectWalletContainer>
          ),
        },
        {
          key: "2",
          label: (
            <ItemMenuProfile
              onClick={() => {
                navigate(ROUTER_CONSTANTS.USER.PROFILE_USER_INNER);
              }}
            >
              Profile
            </ItemMenuProfile>
          ),
        },
        {
          key: "3",
          label: (
            <ItemMenuText
              onClick={() => {
                navigate(ROUTER_CONSTANTS.USER.TRANSACTION_REWARD);
              }}
            >
              Transactions & Rewards
            </ItemMenuText>
          ),
        },
        // {
        //   key: "4",
        //   label: (
        //     <ItemMenuText
        //       onClick={() => {
        //         navigate(ROUTER_CONSTANTS.USER.SUPPORT_TICKET);
        //       }}
        //     >
        //       Support Ticket
        //     </ItemMenuText>
        //   ),
        // },
        {
          key: "5",
          label: (
            <ItemMenuTextLogout
              onClick={async () => {
                logoutAPI()
                  .then(() => {
                    dispatch(authActions.logOut());
                    dispatch(profileActions.removeProfile());
                    clearStorageAndGlobalState(false);
                    navigate(ROUTER_CONSTANTS.USER.LOGIN);
                  })
                  .catch((error) => {});
              }}
            >
              Logout
            </ItemMenuTextLogout>
          ),
        },
      ]}
    />
  );
  return (
    <Container>
      <Row
        align="middle"
        justify="space-between"
        style={{ flexWrap: "nowrap" }}
      >
        <LogoContainer
          onClick={() => {
            navigate("/");
          }}
          xs={12}
          xl={4}
        >
          <NewLogo />
        </LogoContainer>

        {/* Center Navigation - Hidden on Tablet/Mobile (<1200px), Visible on Desktop (>=1200px) */}
        <Col xs={0} xl={14}>
          <Row justify="center" align="middle" gutter={32}>
            <Col>
              <MenuItem
                to={ROUTER_CONSTANTS.HOME}
                state={{
                  isExplore: true,
                }}
                $isActive={isActive(ROUTER_CONSTANTS.HOME)}
              >
                Explore Communities
              </MenuItem>
            </Col>
            {menus.map((item: IMenuItems, key: number) => {
              return (
                <Col key={key}>
                  <MenuItem to={item?.path} $isActive={isActive(item?.path)}>
                    {item?.name}
                  </MenuItem>
                </Col>
              );
            })}
          </Row>
        </Col>

        {/* Right Auth Section - Hamburger on Tablet/Mobile */}
        <LoginContainer xs={12} xl={6}>
          <DesktopAuthWrapper>
            {isLoggedIn && basicInfo ? (
              <LoggedContainer ref={menuRef}>
                <Dropdown
                  getPopupContainer={(node) => menuRef.current || node}
                  overlay={menuLogged}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <ProfileDropdown>
                    {!basicInfo?.avatar && (
                      <AvatarLogged icon={<UserOutlined />} />
                    )}
                    {basicInfo?.avatar && (
                      <AvatarLogged src={basicInfo?.avatar} />
                    )}
                    <ProfileTextContainer>
                      <ProfileText ellipsis>{basicInfo?.full_name}</ProfileText>
                    </ProfileTextContainer>
                    <ArrowDown />
                  </ProfileDropdown>
                </Dropdown>
              </LoggedContainer>
            ) : (
              <>
                <ButtonSignup onClick={onClickSignUp}>Sign up</ButtonSignup>
                <ButtonLogin onClick={onClickLogIn}>Log in</ButtonLogin>
              </>
            )}
          </DesktopAuthWrapper>
          <MobileMenuButton onClick={() => setMobileMenuOpen(true)}>
            <MenuOutlined />
          </MobileMenuButton>
        </LoginContainer>
      </Row>
      <Drawer
        title="Menu"
        placement="right"
        onClose={() => setMobileMenuOpen(false)}
        visible={mobileMenuOpen}
        bodyStyle={{ padding: "20px" }}
      >
        <DrawerExploreLink
          to={ROUTER_CONSTANTS.HOME}
          onClick={() => setMobileMenuOpen(false)}
        >
          Explore Communities
        </DrawerExploreLink>
        {menus.map((item, key) => (
          <DrawerMenuLink
            key={key}
            to={item.path}
            onClick={() => setMobileMenuOpen(false)}
          >
            {item.name}
          </DrawerMenuLink>
        ))}
        <MobileAuthWrapper>
          {isLoggedIn ? (
            <div style={{ padding: "0 10px" }}>
              {/* Profile Header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "20px",
                  cursor: "pointer",
                }}
                onClick={() => {
                  navigate(ROUTER_CONSTANTS.USER.PROFILE_USER_INNER);
                  setMobileMenuOpen(false);
                }}
              >
                {basicInfo?.avatar ? (
                  <AvatarLogged src={basicInfo?.avatar} size={40} />
                ) : (
                  <AvatarLogged icon={<UserOutlined />} size={40} />
                )}
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      margin: 0,
                      fontWeight: 600,
                      fontSize: "16px",
                      color: "#080a25",
                    }}
                  >
                    {basicInfo?.full_name}
                  </p>
                </div>
              </div>

              {/* Connected Wallet Box */}
              <ConnectWalletContainer
                style={{
                  padding: "16px",
                  background: "#f9f9fb",
                  borderRadius: "8px",
                  marginBottom: "20px",
                }}
              >
                <p
                  style={{
                    margin: "0 0 10px 0",
                    color: "#5d5e67",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {account
                    ? isConnected
                      ? `${ethers.utils.formatEther(balance).slice(0, 7)} ETH`
                      : "Wrong Network"
                    : "Connect your wallet"}
                </p>
                {account && (
                  <p
                    style={{
                      margin: "4px 0 0",
                      fontSize: "12px",
                      color: isConnected ? "#52c41a" : "#ff4d4f",
                      fontWeight: 500,
                    }}
                  >
                    🌐 {currentNetworkName}
                    {!isConnected && (
                      <span
                        style={{
                          color: "#ff4d4f",
                          display: "block",
                          marginTop: "2px",
                        }}
                      >
                        Expected: {ActiveNetwork.chainName}
                      </span>
                    )}
                  </p>
                )}
                <BtnConnectWallet
                  onClick={() => {
                    switchWallet();
                    setMobileMenuOpen(false);
                  }}
                  style={{ width: "100%" }}
                >
                  <ConnectWallet />
                  {account ? (
                    !isConnected ? (
                      <span>Switch Network</span>
                    ) : (
                      <span>Disconnect wallet</span>
                    )
                  ) : (
                    <span>Connect wallet</span>
                  )}
                </BtnConnectWallet>
              </ConnectWalletContainer>

              {/* Profile Links Group */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "4px" }}
              >
                <DrawerMenuButton
                  onClick={() => {
                    navigate(ROUTER_CONSTANTS.USER.PROFILE_USER_INNER);
                    setMobileMenuOpen(false);
                  }}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  Profile
                </DrawerMenuButton>
                <DrawerMenuButton
                  onClick={() => {
                    navigate(ROUTER_CONSTANTS.USER.TRANSACTION_REWARD);
                    setMobileMenuOpen(false);
                  }}
                  style={{ borderBottom: "1px solid #f0f0f0" }}
                >
                  Transactions & Rewards
                </DrawerMenuButton>
                <DrawerMenuButton
                  style={{
                    color: "rgba(233, 70, 0, 0.7)",
                    borderBottom: "none",
                  }}
                  onClick={async () => {
                    logoutAPI()
                      .then(() => {
                        dispatch(authActions.logOut());
                        dispatch(profileActions.removeProfile());
                        clearStorageAndGlobalState(false);
                        navigate(ROUTER_CONSTANTS.USER.LOGIN);
                        setMobileMenuOpen(false);
                      })
                      .catch((error) => {});
                  }}
                >
                  Logout
                </DrawerMenuButton>
              </div>
            </div>
          ) : (
            <>
              <MobileDrawerButton
                $isPrimary
                onClick={() => {
                  onClickSignUp();
                  setMobileMenuOpen(false);
                }}
              >
                Sign up
              </MobileDrawerButton>
              <MobileDrawerButton
                onClick={() => {
                  onClickLogIn();
                  setMobileMenuOpen(false);
                }}
              >
                Log in
              </MobileDrawerButton>
            </>
          )}
        </MobileAuthWrapper>
      </Drawer>
      <ConnectWalletModal
        visible={visibleWallet}
        onCancel={() => {
          setVisibleWallet(false);
        }}
        onSuccess={() => {
          setVisibleWallet(false);
        }}
      />
    </Container>
  );
};

export default memo(HeaderUser);
